import {
  BarChart3,
  BookOpen,
  Building2,
  CalendarDays,
  CreditCard,
  Gift,
  Layers,
  FileText,
  LineChart,
  Link2,
  Package,
  PackageSearch,
  Receipt,
  RotateCcw,
  Settings,
  ShoppingBag,
  Shield,
  Sparkles,
  Tag,
  TerminalSquare,
  Ticket,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { DiningTableIcon } from '../components/icons/DiningTableIcon';
import { hasPermission } from '../lib/permissions';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * Permission code(s) required to view this item. When undefined, visible to
   * every authenticated user with scope:read.
   */
  permission?: string | string[];
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

export const navigation: NavSection[] = [
  {
    title: 'Dashboards',
    items: [
      {
        to: '/',
        label: 'Turnover',
        icon: BarChart3,
        permission: ['report:turnover:read', 'report:turnover:current_week:read'],
      },
      {
        to: '/charts',
        label: 'Charts',
        icon: LineChart,
        permission: 'report:chart:read',
      },
      {
        to: '/tax-reports',
        label: 'Tax report',
        icon: FileText,
        permission: 'report:tax:read',
      },
      {
        to: '/consumption-report',
        label: 'Consumption',
        icon: PackageSearch,
        permission: 'report:consumption:read',
      },
    ],
  },
  {
    title: 'Nights',
    items: [
      {
        to: '/nights',
        label: 'Nights',
        icon: CalendarDays,
        permission: 'night:read',
      },
      {
        to: '/blueprints',
        label: 'Blueprints',
        icon: Sparkles,
        permission: 'blueprint:read',
      },
      {
        to: '/tickets',
        label: 'Tickets',
        icon: Ticket,
        permission: 'ticket:read',
      },
      {
        to: '/entrance-scans',
        label: 'Entrance scans',
        icon: Ticket,
        permission: 'entrance:read',
      },
    ],
  },
  {
    title: 'Loyalty',
    items: [
      {
        to: '/vouchers',
        label: 'Vouchers',
        icon: Gift,
        permission: 'voucher:read',
      },
      {
        to: '/voucher-rules',
        label: 'Voucher rules',
        icon: Tag,
        permission: 'voucher:read',
      },
    ],
  },
  {
    title: 'Sales',
    items: [
      {
        to: '/orders',
        label: 'Orders',
        icon: ShoppingBag,
        permission: 'order:read',
      },
      {
        to: '/tabs',
        label: 'Tabs',
        icon: Receipt,
        permission: 'tab:read',
      },
      {
        to: '/payments',
        label: 'Payments',
        icon: Wallet,
        permission: 'payment:read',
      },
      {
        to: '/refunds',
        label: 'Refunds',
        icon: RotateCcw,
        permission: 'refund:read',
      },
      {
        to: '/ledger',
        label: 'Ledger',
        icon: BookOpen,
        permission: 'ledger:read',
      },
      {
        to: '/exact',
        label: 'Exact',
        icon: Building2,
        permission: 'exact:configure',
      },
    ],
  },
  {
    title: 'Catalog',
    items: [
      {
        to: '/products',
        label: 'Products',
        icon: Package,
        permission: 'product:read',
      },
      {
        to: '/categories',
        label: 'Categories',
        icon: Layers,
        permission: 'category:read',
      },
      {
        to: '/discounts',
        label: 'Discounts',
        icon: Tag,
        permission: 'discount:read',
      },
    ],
  },
  {
    title: 'Operations',
    items: [
      {
        to: '/terminals',
        label: 'Terminals',
        icon: CreditCard,
        permission: 'terminal:read',
      },
      {
        to: '/devices',
        label: 'Devices',
        icon: TerminalSquare,
        permission: 'device:read',
      },
      {
        to: '/tables',
        label: 'Tables',
        icon: DiningTableIcon,
        permission: 'table:read',
      },
      {
        to: '/links',
        label: 'Links',
        icon: Link2,
        permission: 'link:read',
      },
    ],
  },
  {
    title: 'Administration',
    items: [
      {
        to: '/users',
        label: 'Users',
        icon: Users,
        permission: 'user:read',
      },
      {
        to: '/roles',
        label: 'Roles',
        icon: Shield,
        permission: 'role:manage',
      },
      {
        to: '/configuration',
        label: 'Configuration',
        icon: Settings,
        permission: [
          'exact:configure',
          'customerio:configure',
          'ticket:configure',
          'loyalty:read',
          'scope:manage',
        ],
      },
      {
        to: '/profile',
        label: 'Profile',
        icon: Users,
      },
    ],
  },
];

const BLOCKED_POST_LOGIN_PREFIXES = ['/login', '/register-passkey'];

/**
 * Same-origin admin path suitable to navigate to after login.
 * Rejects protocol-relative URLs, backslashes, and the public auth routes.
 */
export function safeInternalPath(
  raw: string | null | undefined,
  origin: string = typeof window === 'undefined' ? 'http://localhost' : window.location.origin,
): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed || trimmed.includes('\\') || trimmed.startsWith('//')) return null;
  let url: URL;
  try {
    url = new URL(trimmed, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  if (!url.pathname.startsWith('/') || url.pathname.startsWith('//')) return null;
  if (
    BLOCKED_POST_LOGIN_PREFIXES.some(
      (prefix) => url.pathname === prefix || url.pathname.startsWith(`${prefix}/`),
    )
  ) {
    return null;
  }
  return `${url.pathname}${url.search}${url.hash}`;
}

function navItemForPath(pathname: string): NavItem | undefined {
  const path = pathname.split('?')[0]?.split('#')[0] ?? pathname;
  for (const section of navigation) {
    for (const item of section.items) {
      if (item.to === path) return item;
    }
  }
  return undefined;
}

/**
 * Where to send a manager after a successful passkey login.
 * Priority: the `redirect` query (the page they were on), then their
 * default landing page when they can open that nav item, then the first
 * nav item they can open, then `/profile`.
 */
export function resolvePostLoginPath(input: {
  redirect?: string | null;
  defaultPage?: string | null;
  permissions?: string[] | null;
}): string {
  const redirect = safeInternalPath(input.redirect);
  if (redirect) return redirect;

  const fallback = safeInternalPath(input.defaultPage);
  if (fallback) {
    const item = navItemForPath(fallback);
    if (item && hasPermission(input.permissions, item.permission)) {
      return fallback;
    }
  }

  for (const section of navigation) {
    for (const item of section.items) {
      if (hasPermission(input.permissions, item.permission)) {
        return item.to;
      }
    }
  }
  return '/profile';
}
